/** Chart math ported from the Reddit mockups. All pure. */

export interface Spline {
  /** SVG path `d` string for the smoothed line. */
  line: string;
  /** Coordinates of the last point, `[x, y]`. */
  last: [number, number];
}

/**
 * Smoothed (Catmull-Rom → cubic Bézier) SVG path through already-positioned
 * points. Empty for no points; a bare `M` for one.
 *
 * Exported so a chart that has already laid out its own points (for hit
 * testing, say) can draw the curve without positioning them twice. Every
 * control point is derived only from points inside `pts`, so a curve can never
 * bend toward a point it was not given.
 */
export const splinePath = (pts: Array<[number, number]>): string => {
  const n = pts.length;
  if (n === 0) return '';

  let d = `M${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[i === 0 ? 0 : i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2 < n ? i + 2 : n - 1];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }

  return d;
};

/**
 * Builds a smoothed (Catmull-Rom → cubic Bézier) SVG path for `values`
 * fitted into a `w × h` box with `pad` padding. Y is inverted (SVG space).
 */
export const spline = (values: number[], w: number, h: number, pad: number): Spline => {
  const n = values.length;
  if (n === 0) return { line: '', last: [pad, h - pad] };

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;

  const pts: Array<[number, number]> = values.map((v, i) => {
    const x = n === 1 ? pad + innerW / 2 : pad + (i / (n - 1)) * innerW;
    const y = pad + innerH - ((v - min) / span) * innerH;
    return [x, y];
  });

  return { line: splinePath(pts), last: pts[n - 1] };
};
